import ButtonTsoPq from '@components/Buttons/ButtonTsoPq';
import ChipSelectorTsoPq from '@components/ChipSelector/ChipSelectorTsoPq';
import DatepickerTsoPq from '@components/Datepicker/DatepickerTsoPq';
import { useSidePanel } from '@components/Grid/GridTsoPq';
import ModalDialogTsoPq from '@components/ModalDialog/ModalDialogTsoPq';
import PermissionGate from '@components/PermissionGate/PermissionGate';
import { userOrSupportRestriction } from '@components/PermissionGate/RolePermissionConfig';
import TextFieldTsoPq from '@components/TextField/TextFieldTsoPq';
import TreeAutocompleteTsoPq from '@components/TreeAutocomplete/TreeAutocompleteTsoPq';
import TypographyTsoPq from '@components/Typography/TypographyTsoPq';
import { days } from '@constants/days';
import { parseSubstationDevices } from '@custom-types/TreeTypes';
import type { TreeListItemTsoPqProps } from '@custom-types/TreeTypes';
import {
  StyledLineWrapper,
  StyledMeasurementDeviceTitle,
} from '@pages/MeasurementVisualization/Section/MeasurementVisualizationParameters';
import type { TsosStateProps } from '@pages/MeasurementVisualization/utils';
import { StyledSectionWrapper } from '@pages/MeasurmentExport/MeasurmentExportStyles';
import { StyledButtonWrapper } from '@pages/TrendIdentification/TrendIdentificationStyles';
import { useGetMeasurementParameterGetAllQuery } from '@redux/apiSlices/measurementParameterApi';
import type { ApiCommonExternalDtoMeasurementParameterExternalDto } from '@redux/apiSlices/measurementParameterApi';
import { useLazyGetSubstationGetSubstationDeviceHierarchyQuery } from '@redux/apiSlices/substationApi';
import { usePostTrendJobSubmitMutation } from '@redux/apiSlices/trendJobApi';
import { useGetTsoGetAllQuery } from '@redux/apiSlices/tsoApi';
import { useAppSelector } from '@redux/hooks';
import { setSnackbar } from '@redux/slices/snackbarSlice';
import {
  selectActiveTrend,
  selectAppliedParameters,
  setActiveTrend,
  setActiveTrendName,
  setActiveTrendParameters,
} from '@redux/slices/trendIdentificationSlice';
import type { ActiveTrendParametersProps, SelectedDevicesByTso } from '@redux/slices/trendIdentificationSlice';
import { useLtreeTreeListFromObjects } from '@utils/hooks/useTransfromLtreeInHyerarchy';
import dayjs, { extend as extendDayjs } from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

extendDayjs(utc);

const calculationNameValidationRegex = /^[\p{L}\p{N}\s()/._-]+$/u;

type NestedBoolean = boolean | undefined | { [key: string]: NestedBoolean };

const getDefaultActiveTrend = () => ({
  disabled: false,
  id: Number.NaN,
  label: '',
  value: '',
  waiting: false,
});

const areArraysEqual = (left: string[] = [], right: string[] = []) =>
  left.length === right.length && left.every((value, index) => value === right[index]);

const areTsosEqual = (left: TsosStateProps[] = [], right: TsosStateProps[] = []) =>
  left.length === right.length &&
  left.every(
    (value, index) =>
      value.id === right[index]?.id &&
      value.name === right[index]?.name &&
      value.abbreviation === right[index]?.abbreviation &&
      value.active === right[index]?.active
  );

const areSelectedDevicesEqual = (left: SelectedDevicesByTso = {}, right: SelectedDevicesByTso = {}) => {
  const compareKeys = (leftKey: string, rightKey: string) => leftKey.localeCompare(rightKey);
  const leftKeys = Object.keys(left).sort(compareKeys);
  const rightKeys = Object.keys(right).sort(compareKeys);

  if (!areArraysEqual(leftKeys, rightKeys)) {
    return false;
  }

  return leftKeys.every((key) => areArraysEqual(left[key] ?? [], right[key] ?? []));
};

const areTrendParametersEqual = (left: ActiveTrendParametersProps, right: ActiveTrendParametersProps) =>
  areTsosEqual(left.activeTsos, right.activeTsos) &&
  left.startDate === right.startDate &&
  left.endDate === right.endDate &&
  areArraysEqual(left.measurmentParameters, right.measurmentParameters) &&
  areSelectedDevicesEqual(left.measurmentDevices, right.measurmentDevices);

const SidePanelParameters = () => {
  const { handleOpen } = useSidePanel();

  const appliedParameters = useAppSelector(selectAppliedParameters);
  const activeTrend = useAppSelector(selectActiveTrend);

  const [activeTsos, setActiveTsos] = useState<TsosStateProps[] | []>(appliedParameters.activeTsos);
  const [startDate, setStartDate] = useState<string | undefined>(appliedParameters.startDate);
  const [endDate, setEndDate] = useState<string | undefined>(appliedParameters.endDate);
  const [selectedParameters, setSelectedParameters] = useState<string[]>(appliedParameters.measurmentParameters ?? []);
  const [selectedDevices, setSelectedDevices] = useState<SelectedDevicesByTso>(appliedParameters.measurmentDevices ?? {});
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [tsoDeviceOptions, setTsoDeviceOptions] = useState<{
    [key: number]: TreeListItemTsoPqProps[] | undefined;
  }>({});
  const [showSubmitModal, setShowSubmitModal] = useState<boolean>(false);
  const [modalCalculationName, setModalCalculationName] = useState<string>();
  const [calculationNameError, setCalculationNameError] = useState<string | null>();
  const [treeCollapseSignal, setTreeCollapseSignal] = useState(0);

  const [triggerGetDeviceHierarchy] = useLazyGetSubstationGetSubstationDeviceHierarchyQuery();
  const [submitTrendCalculation] = usePostTrendJobSubmitMutation();

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { data: tsos } = useGetTsoGetAllQuery();
  const { data: measurementParameters } = useGetMeasurementParameterGetAllQuery();

  // validations
  const [mandatoryFieldsStatus, setMandatoryFieldsStatus] = useState<{
    activeTsos: boolean | undefined;
    startDate: boolean | undefined;
    endDate: boolean | undefined;
    selectedParameters: boolean | undefined;
    selectedDevices: { [key: string]: boolean | undefined };
  }>({
    activeTsos: undefined,
    startDate: undefined,
    endDate: undefined,
    selectedParameters: undefined,
    selectedDevices: {},
  });

  const prevActiveTsosRef = useRef<TsosStateProps[]>([]);
  const latestParametersValues = useRef<ActiveTrendParametersProps>({
    activeTsos: appliedParameters.activeTsos,
    startDate: appliedParameters.startDate,
    endDate: appliedParameters.endDate,
    measurmentParameters: appliedParameters.measurmentParameters ?? [],
    measurmentDevices: appliedParameters.measurmentDevices ?? {},
  });

  const selectedTrendName = useAppSelector((state) => state.trendIdentification.trendName);

  const filterParameters = () => {
    const parameters = measurementParameters?.externalDtos?.filter((p) => p.isUsedForTrendCalculator) ?? [];
    let filteredParameters: ApiCommonExternalDtoMeasurementParameterExternalDto[] | undefined = parameters;
    filteredParameters = filteredParameters.filter((parameter) => parameter.categoryPath?.split('.'));

    return filteredParameters;
  };

  const parameters = useLtreeTreeListFromObjects(filterParameters(), undefined, {
    '10minutes': 1,
    '2hours': 1,
  });

  const clearSelectedCalculation = () => {
    if (!selectedTrendName && Number.isNaN(activeTrend.id)) return;

    dispatch(setActiveTrend(getDefaultActiveTrend()));
    dispatch(setActiveTrendName(''));
  };

  const handleActiveTsosChange = (tsos: TsosStateProps[]) => {
    clearSelectedCalculation();

    const selectedTsosIds = new Set(tsos.map((tso) => `${tso.id}`));
    const nextSelectedDevices = Object.entries(selectedDevices).reduce<SelectedDevicesByTso>((accumulator, [key, value]) => {
      if (selectedTsosIds.has(key)) {
        accumulator[key] = value;
      }
      return accumulator;
    }, {});
    const mandatorySelectedDevices = Object.fromEntries(
      tsos.map((tso) => [tso.id, !!nextSelectedDevices[tso.id]?.length])
    );

    setSelectedDevices(nextSelectedDevices);
    setActiveTsos(tsos);
    latestParametersValues.current.activeTsos = tsos;
    latestParametersValues.current.measurmentDevices = nextSelectedDevices;

    setMandatoryFieldsStatus((prev) => ({
      ...prev,
      activeTsos: formSubmitted ? !!tsos.length : prev.activeTsos,
      selectedDevices: mandatorySelectedDevices,
    }));
  };

  const handleStartChange = (date: dayjs.Dayjs) => {
    clearSelectedCalculation();

    setStartDate(dayjs(date).format('YYYY-MM-DD HH:mm'));
    latestParametersValues.current.startDate = dayjs(date).format('YYYY-MM-DD HH:mm');
    setMandatoryFieldsStatus((prev) => ({ ...prev, startDate: !!date }));
  };

  const handleEndChange = (date: dayjs.Dayjs) => {
    clearSelectedCalculation();

    setEndDate(dayjs(date).format('YYYY-MM-DD HH:mm'));
    latestParametersValues.current.endDate = dayjs(date).format('YYYY-MM-DD HH:mm');
    setMandatoryFieldsStatus((prev) => ({ ...prev, endDate: !!date }));
  };

  const handleParametersChange = (selectedParameters: string[], isUserChange: boolean | undefined) => {
    if (isUserChange) {
      clearSelectedCalculation();
    }

    setSelectedParameters(selectedParameters);
    latestParametersValues.current.measurmentParameters = selectedParameters;
    setMandatoryFieldsStatus((prev) => ({ ...prev, selectedParameters: !!selectedParameters.length }));
  };

  const handleDevicesChange = (id: number, devices: string[], isUserChange: boolean | undefined) => {
    if (isUserChange) {
      clearSelectedCalculation();
    }

    if (!id) return;

    setSelectedDevices((prev) => {
      const nextDevices = {
        ...prev,
        [id]: devices,
      };

      latestParametersValues.current.measurmentDevices = nextDevices;
      return nextDevices;
    });

    setMandatoryFieldsStatus((prev) => ({
      ...prev,
      selectedDevices: { ...prev.selectedDevices, [id]: devices.length > 0 },
    }));
  };

  const handleDiscard = () => {
    setFormSubmitted(false);
    setActiveTsos([]);
    setStartDate(undefined);
    setEndDate(undefined);
    setSelectedParameters([]);
    setSelectedDevices({});
    setTreeCollapseSignal((previous) => previous + 1);
    setMandatoryFieldsStatus({
      activeTsos: true,
      startDate: true,
      endDate: true,
      selectedParameters: true,
      selectedDevices: {},
    });
    dispatch(
      setActiveTrend({
        disabled: false,
        id: Number.NaN,
        label: '',
        value: '',
        waiting: false,
      })
    );
    dispatch(setActiveTrendName(''));
  };

  const handleSubmit = () => {
    setFormSubmitted(true);

    const currentMandatoryStatus = {
      activeTsos: activeTsos.length > 0,
      startDate: !!startDate,
      endDate: !!endDate,
      selectedParameters: !!(selectedParameters?.length && selectedParameters?.length > 0),
      selectedDevices: Object.fromEntries(activeTsos.map((tso) => [tso.id, !!selectedDevices[tso.id]?.length])),
    };

    setMandatoryFieldsStatus(currentMandatoryStatus);

    const allMandatoryFieldsFilled = (mandatory: NestedBoolean): boolean =>
      typeof mandatory === 'object' && mandatory !== null
        ? Object.values(mandatory).reduce<boolean>((acc, value) => acc && allMandatoryFieldsFilled(value), true)
        : mandatory === true;

    if (allMandatoryFieldsFilled(currentMandatoryStatus)) {
      handleOpen(false);
      setShowSubmitModal(true);
    }
  };

  const validateName = useCallback((name?: string) => {
    const trimmedName = name?.trim() ?? '';

    if (!trimmedName) {
      return t('trendIdentification.dialogTextfieldValidationInvalidName');
    }

    if (!calculationNameValidationRegex.test(trimmedName)) {
      return t('trendIdentification.dialogTextfieldValidationError');
    }

    return null;
  }, [t]);

  const handleCalculationSubmit = async () => {
    const sanitizedCalculationName = modalCalculationName?.trim() ?? '';
    const error = validateName(modalCalculationName);
    if (error) {
      setCalculationNameError(error);
      return;
    }
    setCalculationNameError(null);

    const startToUTC = dayjs.utc(startDate).startOf('day').toISOString();
    const endToUTC = dayjs.utc(endDate).endOf('day').toISOString();
    const measurementParametersIds =
      (selectedParameters
        ?.map((techName) => measurementParameters?.externalDtos?.find((p) => p.technicalName === techName)?.id)
        .filter(Boolean) as number[]) ?? [];
    const selectedDeviceIds = Object.values(selectedDevices)
      .filter(Array.isArray)
      .flat()
      .filter((fullId): fullId is string => !!fullId)
      .map((fullId) => Number(fullId.split('-').at(-1)))
      .filter(Boolean);

    try {
      const payload = {
        name: sanitizedCalculationName,
        start: startToUTC,
        end: endToUTC,
        measurementParameterIds: measurementParametersIds,
        measurementDeviceIds: selectedDeviceIds,
      };
      await submitTrendCalculation(payload);
      dispatch(
        setSnackbar({
          open: true,
          type: 'success',
          textContent: `${t('trendIdentification.modal.submitCalculation.submit.success')}`,
        })
      );
      handleDiscard();
      setShowSubmitModal(false);
    } catch (error) {
      dispatch(
        setSnackbar({
          open: true,
          type: 'error',
          textContent: `${t('trendIdentification.modal.submitCalculation.submit.error')}`,
        })
      );
      console.error(error);
    }
  };

  const fetchDevicesForTso = useCallback(async (tsoId: number) => {
    try {
      const result = await triggerGetDeviceHierarchy(tsoId, true);
      const rawTree = result.data?.externalDtos;
      if (!rawTree) return;

      const parsedTree = parseSubstationDevices(rawTree);
      if (!parsedTree) return;

      setTsoDeviceOptions((prev) => ({ ...prev, [tsoId]: parsedTree }));
    } catch (error) {
      console.error(error);
    }
  }, [triggerGetDeviceHierarchy]);

  useEffect(() => {
    if (modalCalculationName === undefined) {
      setCalculationNameError(null);
      return;
    }

    const validationResult = validateName(modalCalculationName);
    setCalculationNameError(validationResult);
  }, [modalCalculationName, validateName]);

  useEffect(() => {
    if (!showSubmitModal) {
      setModalCalculationName(undefined);
      setCalculationNameError(null);
    }
  }, [showSubmitModal]);

  useEffect(() => {
    const prevTsoIds = new Set(prevActiveTsosRef.current.map((tso) => tso.id));
    const newTsos = activeTsos.filter((tso) => !prevTsoIds.has(tso.id));

    const loadMissingTsoDevices = async () => {
      for (const tso of newTsos) {
        if (!tso || tsoDeviceOptions[tso.id]) continue;
        await fetchDevicesForTso(tso.id);
      }
    };

    loadMissingTsoDevices();

    prevActiveTsosRef.current = activeTsos;
  }, [activeTsos, fetchDevicesForTso, tsoDeviceOptions]);

  useEffect(() => {
    const nextAppliedParameters: ActiveTrendParametersProps = {
      activeTsos: appliedParameters.activeTsos,
      startDate: appliedParameters.startDate,
      endDate: appliedParameters.endDate,
      measurmentParameters: appliedParameters.measurmentParameters ?? [],
      measurmentDevices: appliedParameters.measurmentDevices ?? {},
    };

    if (areTrendParametersEqual(nextAppliedParameters, latestParametersValues.current)) {
      return;
    }

    setActiveTsos(nextAppliedParameters.activeTsos);
    setStartDate(nextAppliedParameters.startDate);
    setEndDate(nextAppliedParameters.endDate);
    setSelectedParameters(nextAppliedParameters.measurmentParameters);
    setSelectedDevices(nextAppliedParameters.measurmentDevices);
    setTreeCollapseSignal((previous) => previous + 1);
  }, [appliedParameters]);

  useEffect(() => {
    latestParametersValues.current = {
      activeTsos: activeTsos,
      startDate: startDate ?? '',
      endDate: endDate ?? '',
      measurmentParameters: selectedParameters ?? [],
      measurmentDevices: selectedDevices ?? {},
    };
  }, [activeTsos, startDate, endDate, selectedParameters, selectedDevices]);

  useEffect(() => {
    return () => {
      if (latestParametersValues.current) {
        dispatch(
          setActiveTrendParameters({
            activeTsos: latestParametersValues.current.activeTsos,
            startDate: latestParametersValues.current.startDate,
            endDate: latestParametersValues.current.endDate,
            measurmentParameters: latestParametersValues.current.measurmentParameters,
            measurmentDevices: latestParametersValues.current.measurmentDevices,
          })
        );
      }
    };
  }, [dispatch]);

  return (
    <>
      <StyledSectionWrapper>
        <StyledMeasurementDeviceTitle>
          {t('trendIdentification.sidePanel.parameters.title')}
        </StyledMeasurementDeviceTitle>
        <ChipSelectorTsoPq
          customWidth="30.5rem"
          label={t('trendIdentification.sidePanel.parameters.tso.label')}
          chips={tsos?.externalDtos ?? []}
          activeChips={activeTsos}
          handleChange={handleActiveTsosChange}
          multiple
          mandatory
          error={mandatoryFieldsStatus.activeTsos === false}
          supportingText={
            mandatoryFieldsStatus.activeTsos === false
              ? `${t('trendIdentification.sidePanel.parameters.tso.label')} ${t('general.error.required')}`
              : ''
          }
        ></ChipSelectorTsoPq>
        <StyledLineWrapper>
          <DatepickerTsoPq
            label={t('trendIdentification.sidePanel.parameters.start.label')}
            type="datepicker"
            width="15rem"
            disabled={!endDate}
            enableFutureDates={false}
            shouldDisableDate={(date) => date.day() !== days.Mon}
            maxDateTime={endDate ? dayjs(endDate).subtract(106, 'week') : undefined}
            referenceDate={dayjs(endDate).subtract(106, 'week')}
            date={startDate}
            handleChange={handleStartChange}
            mandatory
            error={mandatoryFieldsStatus.startDate === false}
            info={t('trendIdentification.sidePanel.parameters.start.tooltip')}
            supportingText={t('trendIdentification.sidePanel.parameters.start.supportingText')}
          />
          <DatepickerTsoPq
            label={t('trendIdentification.sidePanel.parameters.end.label')}
            type="datepicker"
            width="15rem"
            enableFutureDates={false}
            shouldDisableDate={(date) => date.day() !== days.Sun}
            handleChange={handleEndChange}
            date={endDate}
            mandatory
            error={mandatoryFieldsStatus.endDate === false}
            supportingText={t('trendIdentification.sidePanel.parameters.end.supportingText')}
          />
        </StyledLineWrapper>
        <TreeAutocompleteTsoPq
          customwidth="31.5rem"
          options={parameters ?? []}
          label={t('trendIdentification.sidePanel.parameters.measurmentParameters.label')}
          placeholder={t('general.error.valueSelect')}
          collapseSignal={treeCollapseSignal}
          handleChange={(selected, _, isUserChange) => {
            handleParametersChange(selected, isUserChange);
          }}
          defaultValue={selectedParameters}
          mandatory
          error={mandatoryFieldsStatus.selectedParameters === false}
          supportingText={
            mandatoryFieldsStatus.selectedParameters === false
              ? `${t('trendIdentification.sidePanel.parameters.measurmentParameters.label')} ${t('general.error.required')}`
              : ''
          }
        />
      </StyledSectionWrapper>
      {activeTsos?.map((tso) => (
        <StyledSectionWrapper key={tso.id} selectDevice>
          <StyledMeasurementDeviceTitle>{tso.name}</StyledMeasurementDeviceTitle>
          <TreeAutocompleteTsoPq
            customwidth="32rem"
            options={tsoDeviceOptions[tso.id] ?? []}
            label={t('trendIdentification.sidePanel.parameters.measurmentDevice.label')}
            placeholder={t('general.error.valueSelect')}
            collapseSignal={treeCollapseSignal}
            handleChange={(selected, _, isUserChange) => {
              handleDevicesChange(tso.id, selected, isUserChange);
            }}
            defaultValue={selectedDevices[tso.id] ?? []}
            lowestLevel={3}
            multiple
            mandatory
            error={mandatoryFieldsStatus.selectedDevices[tso.id] === false}
            supportingText={
              mandatoryFieldsStatus.selectedDevices[tso.id] === false
                ? `${t('measurementVisualization.parameters.measurementDevice')} ${t('general.error.required')}`
                : ''
            }
          />
        </StyledSectionWrapper>
      ))}
      <StyledButtonWrapper>
        <ButtonTsoPq
          onClick={handleDiscard}
          sizeVariant="m"
          buttonType="outlinedPrimary"
          textContent={t('trendIdentification.sidePanel.parameters.reset.label')}
        />
        <PermissionGate roles={userOrSupportRestriction}>
          <ButtonTsoPq
            onClick={handleSubmit}
            sizeVariant="m"
            buttonType="filledPrimary"
            textContent={t('trendIdentification.sidePanel.parameters.submit.label')}
            disabled={!!selectedTrendName}
          />
        </PermissionGate>
      </StyledButtonWrapper>

      <ModalDialogTsoPq
        withExtraField
        title={t('trendIdentification.modal.submitCalculation.title')}
        modalState={showSubmitModal}
        customMaxWidth="43.1875rem"
        icon
        buttons
        onClose={() => setShowSubmitModal(false)}
        actions={
          <>
            <ButtonTsoPq
              buttonType="outlinedPrimary"
              onClick={() => setShowSubmitModal(false)}
              textContent={t('trendIdentification.modal.submitCalculation.cancel')}
            />
            <ButtonTsoPq
              buttonType="filledPrimary"
              onClick={() => handleCalculationSubmit()}
              textContent={t('trendIdentification.modal.submitCalculation.submit')}
              disabled={!!calculationNameError || !modalCalculationName?.trim()}
            />
          </>
        }
      >
        <TypographyTsoPq typographyType="bodyTextMD">
          {t('trendIdentification.modal.submitCalculation.description')}
        </TypographyTsoPq>
        <TextFieldTsoPq
          label={t('trendIdentification.modal.submitCalculation.nameField.label')}
          placeholder={t('trendIdentification.modal.submitCalculation.nameField.placeholder')}
          customwidth="41rem"
          mandatory
          value={modalCalculationName ?? ''}
          handleChange={(e) => setModalCalculationName(e)}
          supportingText={
            calculationNameError || t('trendIdentification.modal.submitCalculation.nameField.supportingText')
          }
          whiteBackground
          error={!!calculationNameError}
          characterLimit={250}
        />
      </ModalDialogTsoPq>
    </>
  );
};

export default SidePanelParameters;
